import { useState, useEffect, useRef } from "react";
import { uiAlert } from "../../../../components/alerts/customAlert/uiAlert";
import { hasSeenPromptsSubUnitUrl, updateSubUnitUrl } from "../../../../api/endpoints";
import { ButtonLoaderII } from "../../../../components/uxCollections/Loaders";
import { getAllData, saveData } from "../../../../tools/indexdb";

export default function SubUnitOverlay({ productToEdit, closeModal, updateSelectedProduct, storeID, storeName, currency }) {
  const [stage, setStage] = useState("question");
  const [subUnits, setSubUnits] = useState([]);
  const [form, setForm] = useState({ name: "", price: "", quantity: "" });
  const [formError, setFormError] = useState("");
  const [slideIn, setSlideIn] = useState(false);
  const [isWide, setIsWide] = useState(false);
  const [loading, setloading] = useState(false);

  useEffect(() => {
    const check = () => setIsWide(window.innerWidth >= 640);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const openManager = () => {
    setStage("manager");
    setTimeout(() => setSlideIn(true), 10);
  };
  const openForm = () => setStage("addForm");
  const backToManager = () => setStage("manager");

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setFormError("");
  };

  const handleAdd = () => {
    if (!form.name.trim() || !form.price || !form.quantity) {
      setFormError("All fields are required.");
      return;
    }
    if (isNaN(form.price) || isNaN(form.quantity) || Number(form.quantity) < 1) {
      setFormError("Price and quantity must be valid positive numbers.");
      return;
    }
    setSubUnits([...subUnits, { ...form, id: `${Date.now()}_${Math.random().toString(36).substring(2, 9)}` }]);
    setForm({ name: "", price: "", quantity: "" });
    setStage("manager");
  };

  const remove = (id) => setSubUnits(subUnits.filter((s) => s.id !== id));

  const reset = () => {
    /* setStage("question"); */
    closeModal();
    setSlideIn(false);
    setSubUnits([]);
    setForm({ name: "", price: "", quantity: "" });
    setFormError("");
  };

  // hasSeenPromptToSub

  const noSubUnitRequired = async () => {
    setloading(true);
    try {
      const url = `${hasSeenPromptsSubUnitUrl}${storeID}/${productToEdit._id}`;
      const payload = {
        employee: null,
      };
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.status === 404) {
        uiAlert(data.message, "error");
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || "Failed to update product.");
      }

      let { storeProductCollection, modifiedProduct } = data;

      updateSelectedProduct(modifiedProduct);

      let storeData = await getAllData(storeName);
      if (storeData) {
        storeData.storeProductCollection = storeProductCollection.products;

        await saveData(storeName, storeData);
      }
      uiAlert("No sub units will be configured for this product.", "success");
      setStage("done");
    } catch (error) {
      console.log(error);
      uiAlert("Something went wrong with your request. Please try again", "error");
    } finally {
      setloading(false);
    }
  };

  const createSubUnits = async () => {
    setloading(true);
    try {
      const url = `${updateSubUnitUrl}${storeID}/${productToEdit._id}`;
      const payload = {
        subUnits: subUnits.map(({ name, price, quantity, id }) => ({
          name,
          price: Number(price),
          quantity: Number(quantity),
          id,
        })),
        costPrice: productToEdit.costPrice,
        employee: null,
      };

      let response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (response.status === 404) {
        uiAlert(data.message, "error");
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || "Failed to update product.");
      }

      let { storeProductCollection, modifiedProduct } = data;
      updateSelectedProduct(modifiedProduct);

      let storeData = await getAllData(storeName);
      if (storeData) {
        storeData.storeProductCollection = storeProductCollection.products;

        await saveData(storeName, storeData);
      }
      uiAlert("Your sub units have been registered", "success");
      setStage("done");
    } catch (error) {
      console.log(error);
      uiAlert("Something went wrong with your request. Please try again", "error");
    } finally {
      setloading(false);
    }

    // setStage("done");
  };

  // Sheet position: bottom sheet on mobile, centered modal on desktop
  const sheetPos = isWide
    ? {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: slideIn ? "translate(-50%,-50%) scale(1)" : "translate(-50%,-47%) scale(0.96)",
        opacity: slideIn ? 1 : 0,
        width: "min(520px, 90vw)",
        maxHeight: "85vh",
        borderRadius: 24,
        boxShadow: "0 32px 80px rgba(0,0,0,0.22)",
        transition: "transform 0.38s cubic-bezier(0.32,0.72,0,1), opacity 0.28s ease",
      }
    : {
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        transform: slideIn ? "translateY(0)" : "translateY(100%)",
        borderRadius: "24px 24px 0 0",
        maxHeight: "88dvh",
        transition: "transform 0.42s cubic-bezier(0.32,0.72,0,1)",
        boxShadow: "0 -12px 50px rgba(0,0,0,0.18)",
      };

  const questionPos = isWide
    ? {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%,-50%)",
        width: "min(400px, 88vw)",
        borderRadius: 20,
        boxShadow: "0 24px 70px rgba(0,0,0,0.22)",
      }
    : {
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        borderRadius: "24px 24px 0 0",
      };

  return (
    <>
      <style>{`
      
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
       

        .page {
          min-height: 100vh;
          background: linear-gradient(135deg, #e8edf5, #dde5f0);
          display: flex; align-items: center; justify-content: center;
          padding: 20px;
        }

        /* ── Product card (demo bg) ── */
        .pcard {
          background: #fff;
          border-radius: 20px;
          padding: clamp(20px, 5vw, 32px) clamp(16px, 4vw, 28px);
          width: min(340px, 100%);
          box-shadow: 0 4px 28px rgba(0,0,0,0.09);
          display: flex; flex-direction: column; align-items: center; gap: 10px;
        }
        .pcard-img {
          width: clamp(90px, 28vw, 120px);
          height: clamp(80px, 24vw, 106px);
          background: #f1f5f9; border-radius: 14px;
          display: flex; align-items: center; justify-content: center;
          border: 1px solid #e2e8f0;
        }
        .pcard-name { font-size: clamp(16px, 4.5vw, 20px); font-weight: 800; color: #1e293b; }
        .pcard-cat  { font-size: clamp(11px, 3vw, 13px); color: #94a3b8; font-weight: 500; }
        .pcard-stock {
          background: #fef9ec; color: #d97706;
          border: 1px solid #fde68a; border-radius: 10px;
          padding: 5px 14px; font-size: clamp(13px, 3.5vw, 15px); font-weight: 700;
        }
        .pcard-prices { display: flex; gap: clamp(16px, 5vw, 28px); flex-wrap: wrap; justify-content: center; }
        .pcard-price-item { text-align: center; }
        .pcard-price-label { font-size: 10px; color: #94a3b8; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; }
        .pcard-price-val   { font-size: clamp(13px, 3.5vw, 15px); font-weight: 700; color: #1e293b;  }

        /* ── Backdrop ── */
        .backdrop {
          position: fixed; inset: 0; z-index: 40;
          background: rgba(10,18,40,0.52);
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
          animation: fadeIn 0.2s ease;
          z-index: 900;
        }
        @keyframes fadeIn { from { opacity:0 } to { opacity:1 } }
        @keyframes popIn  { from { opacity:0; transform: translate(-50%,-50%) scale(0.95) } to { opacity:1; transform: translate(-50%,-50%) scale(1) } }

        /* ── Question box ── */
        .qbox {
          background: #fff;
          z-index: 1000;
          display: flex; flex-direction: column; align-items: center;
          gap: clamp(10px, 3vw, 16px);
          padding: clamp(24px, 5vw, 36px) clamp(18px, 5vw, 28px) clamp(22px, 5vw, 32px);
          text-align: center;
         {/*  animation: popIn 0.28s ease forwards; */}
        }
        .qicon {
          width: clamp(50px, 14vw, 64px); height: clamp(50px, 14vw, 64px);
          border-radius: 50%;
          background: linear-gradient(135deg, #dbeafe, #eff6ff);
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 4px 18px rgba(37,99,235,0.15);
        }
        .qtitle {
          font-size: clamp(15px, 4.5vw, 20px); font-weight: 800; color: #0c1a3a; line-height: 1.25;
        }
        .qsub {
          font-size: clamp(12px, 3.2vw, 14px); color: #64748b; line-height: 1.6; max-width: 300px;
        }
        .qactions { display: flex; gap: 10px; width: 100%; margin-top: 4px; }
        @media (max-width: 360px) { .qactions { flex-direction: column; } }

        /* ── Buttons ── */
        .btn-outline {
          flex: 1; padding: clamp(10px, 3vw, 13px) 8px;
          border-radius: 12px; border: 1.5px solid #e2e8f0;
          background: #fff; color: #475569;
           font-weight: 600;
          font-size: clamp(12px, 3.2vw, 14px); cursor: pointer;
          transition: background .15s, border-color .15s; white-space: nowrap;
        }
        .btn-outline:hover { background: #f8fafc; border-color: #c0cfe0; }

        .btn-primary {
          flex: 1; padding: clamp(10px, 3vw, 13px) 8px;
          border-radius: 12px; background: #2563eb; border: none;
          color: #fff;  font-weight: 700;
          font-size: clamp(12px, 3.2vw, 14px); cursor: pointer;
          box-shadow: 0 4px 16px rgba(37,99,235,0.35);
          transition: background .15s, transform .1s; white-space: nowrap;
        }
        .btn-primary:hover { background: #1d4ed8; transform: translateY(-1px); }
        .btn-primary:active { transform: none; }

        .btn-full {
          width: 100%; padding: clamp(12px, 3.5vw, 15px);
          border-radius: 14px; background: #2563eb; border: none;
          color: #fff; font-weight: 700;
          font-size: clamp(13px, 3.5vw, 15px); cursor: pointer;
          box-shadow: 0 4px 20px rgba(37,99,235,0.3);
          transition: background .15s, transform .1s; flex-shrink: 0;
        }
        .btn-full:hover { background: #1d4ed8; transform: translateY(-1px); }
        .btn-full:active { transform: none; }

        /* ── Sheet ── */
        .sheet {
          background: #fff; z-index: 50;
          overflow-y: auto; display: flex; flex-direction: column;
          padding: 0 clamp(16px, 5vw, 28px) clamp(24px, 6vw, 36px);
          z-index: 1000;
        }
        .handle {
          width: 40px; height: 4px; border-radius: 2px;
          background: #e2e8f0; margin: 14px auto 18px; flex-shrink: 0;
        }
        .sheet-hdr {
          display: flex; justify-content: space-between; align-items: flex-start;
          margin-bottom: clamp(14px, 4vw, 20px); gap: 10px; flex-wrap: wrap;
        }
        .sheet-lbl {
          font-size: 11px; font-weight: 700; color: #94a3b8;
          text-transform: uppercase; letter-spacing: .1em;
        }
        .sheet-name {
          font-size: clamp(18px, 5vw, 26px); font-weight: 800; color: #0c1a3a; margin-top: 2px;
        }
        .base-tag {
          background: #f0fdf4; color: #16a34a;
          border: 1px solid #bbf7d0; border-radius: 8px;
          padding: 5px 12px;
          font-size: clamp(11px, 2.8vw, 13px); font-weight: 600;
          white-space: nowrap; align-self: flex-start; margin-top: 4px;
        }

        /* ── Sub unit list ── */
        .su-list { display: flex; flex-direction: column; gap: 8px; min-height: 56px; margin-bottom: 14px; }
        .su-empty {
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          padding: clamp(20px, 5vw, 30px) 0; gap: 10px;
        }
        .su-empty-txt { font-size: clamp(12px, 3vw, 13px); color: #94a3b8; font-weight: 500; }

        .su-row {
          display: flex; align-items: center; gap: 10px;
          background: #f8fafc; border-radius: 14px;
          padding: clamp(10px, 3vw, 13px) clamp(12px, 3.5vw, 16px);
          border: 1px solid #e2e8f0;
          transition: border-color .15s;
        }
        .su-row:hover { border-color: #bfd3f8; }
        .su-num {
          width: 26px; height: 26px; border-radius: 7px;
          background: #eff6ff; color: #2563eb;
          display: flex; align-items: center; justify-content: center;
          font-size: 11px; font-weight: 800; flex-shrink: 0;
        }
        .su-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
        .su-n {
          font-size: clamp(13px, 3.5vw, 15px); font-weight: 700; color: #1e293b;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .su-m {
          font-size: clamp(10px, 2.5vw, 12px); color: #94a3b8;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .su-price {
          font-size: clamp(11px, 3vw, 14px); font-weight: 700; color: #059669;
           flex-shrink: 0;
        }
        .su-del {
          width: 28px; height: 28px; border-radius: 7px;
          background: #fff1f2; border: none; color: #f43f5e;
          cursor: pointer; display: flex; align-items: center; justify-content: center;
          flex-shrink: 0; transition: background .15s, transform .1s;
        }
        .su-del:hover { background: #ffe4e6; transform: scale(1.1); }

        .add-btn {
          display: flex; align-items: center; justify-content: center; gap: 8px;
          width: 100%; padding: clamp(11px, 3vw, 14px);
          border-radius: 14px; border: 1.5px dashed #93c5fd;
          background: #eff6ff; color: #2563eb;
           font-weight: 700;
          font-size: clamp(12px, 3.5vw, 14px); cursor: pointer;
          margin-bottom: 12px; transition: background .15s, border-color .15s;
        }
        .add-btn:hover { background: #dbeafe; border-color: #60a5fa; }
        .add-count {
          margin-left: auto; background: #dbeafe; border-radius: 6px;
          padding: 2px 8px; font-size: 11px; font-weight: 800; color: #1d4ed8;
        }
        .limit-msg {
          text-align: center; font-size: clamp(11px, 3vw, 13px); color: #d97706;
          background: #fffbeb; padding: 10px 14px; border-radius: 12px;
          border: 1px solid #fde68a; margin-bottom: 12px; font-weight: 600;
        }

        /* ── Form ── */
        .form-hdr { display: flex; align-items: center; gap: 12px; margin-bottom: clamp(16px, 4vw, 22px); }
        .back-btn {
          width: 38px; height: 38px; border-radius: 11px;
          background: #eff6ff; border: none;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; flex-shrink: 0; transition: background .15s;
        }
        .back-btn:hover { background: #dbeafe; }
        .form-body { display: flex; flex-direction: column; gap: 6px; }
        .f-label {
          font-size: clamp(11px, 2.8vw, 13px); font-weight: 700; color: #475569; margin-top: 10px; line-height: 1.4;
        }
        .f-input {
          width: 100%; padding: clamp(11px, 3vw, 13px) 14px;
          border-radius: 12px; border: 1.5px solid #e2e8f0;
          font-size: clamp(13px, 3.5vw, 15px);
          color: #1e293b; background: #f8fafc; outline: none;
          transition: border-color .2s, box-shadow .2s;
          -webkit-appearance: none;
        }
        .f-input:focus {
          border-color: #93c5fd;
          box-shadow: 0 0 0 3px rgba(147,197,253,.22);
          background: #fff;
        }
        .preview {
          display: flex; align-items: center; gap: 7px;
          background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 11px;
          padding: 9px 13px; flex-wrap: wrap;
          font-size: clamp(11px, 3vw, 13px); color: #15803d; font-weight: 600;
          margin-top: 6px; line-height: 1.5;
        }
        .f-error { font-size: clamp(11px, 2.8vw, 13px); color: #ef4444; margin-top: 4px; font-weight: 500; }

        /* ── Done modal ── */
        .done-modal {
          position: fixed; 
          top: 50%; left: 50%; transform: translate(-50%,-50%);
          background: #fff; border-radius: 20px;
          width: min(320px, 88vw);
          padding: clamp(24px, 6vw, 32px);
          box-shadow: 0 24px 70px rgba(0,0,0,0.22);
          display: flex; flex-direction: column; align-items: center; gap: 12px;
          text-align: center;
          animation: popIn 0.28s ease forwards;
          z-index: 1000;
        }
        .done-icon {
          width: 56px; height: 56px; border-radius: 50%;
          background: #f0fdf4; display: flex; align-items: center; justify-content: center;
        }
        .done-title { font-size: clamp(16px, 4.5vw, 20px); font-weight: 800; color: #1e293b; }
        .done-sub   { font-size: clamp(12px, 3vw, 14px); color: #64748b; line-height: 1.5; }
      `}</style>

      {/* ── Page background ── */}
      {/*    <div className='page'>
        <div className='pcard'>
          <div className='pcard-img'>
            <svg width='44' height='44' viewBox='0 0 24 24' fill='none' stroke='#bbb' strokeWidth='1.4'>
              <rect x='3' y='3' width='18' height='18' rx='2' />
              <circle cx='8.5' cy='8.5' r='1.5' />
              <path d='M21 15l-5-5L5 21' />
            </svg>
          </div>
          <p className='pcard-name'>Nta</p>
          <p className='pcard-cat'>Cameras & Photography</p>
          <div className='pcard-stock'>700 in stock</div>
          <div className='pcard-prices'>
            <div className='pcard-price-item'>
              <div className='pcard-price-label'>Cost price</div>
              <div className='pcard-price-val'>kr 35,000</div>
            </div>
            <div className='pcard-price-item'>
              <div className='pcard-price-label'>Unit price</div>
              <div className='pcard-price-val'>kr 40,000</div>
            </div>
          </div>
        </div>
      </div> */}

      {/* ── Backdrop ── */}
      {(stage === "question" || stage === "manager" || stage === "addForm" || stage === "done") && <div onClick={reset} className='backdrop' />}

      {/* ── Question ── */}
      {stage === "question" && (
        <div className='qbox' style={questionPos}>
          <div className='qicon'>
            <svg width='28' height='28' viewBox='0 0 24 24' fill='none' stroke='#2563eb' strokeWidth='2'>
              <circle cx='12' cy='12' r='10' />
              <path d='M12 8v4M12 16h.01' />
            </svg>
          </div>
          <h2 className='qtitle'>Does this product have sub units?</h2>
          <p className='qsub'>
            Sub units let you sell smaller parts of <strong>{productToEdit.name}</strong> at different prices.
          </p>
          <div className='qactions'>
            <button disabled={loading} className='btn-outline' onClick={() => noSubUnitRequired()}>
              {loading ? <ButtonLoaderII color='#2563eb' /> : "No sub units"}
            </button>
            <button className='btn-primary' onClick={openManager}>
              Create sub unit
            </button>
          </div>
        </div>
      )}

      {/* ── Done ── */}
      {stage === "done" && (
        <div className='done-modal'>
          <div className='done-icon'>
            <svg width='28' height='28' viewBox='0 0 24 24' fill='none' stroke='#16a34a' strokeWidth='2.5'>
              <path d='M20 6L9 17l-5-5' />
            </svg>
          </div>
          <p className='done-title'>All set!</p>
          <p className='done-sub'>
            {subUnits.length > 0
              ? `${subUnits.length} sub unit${subUnits.length > 1 ? "s" : ""} saved for ${productToEdit.name}.`
              : `No sub units configured for ${productToEdit.name}.`}
          </p>
          <button className='btn-full' style={{ marginTop: 4 }} onClick={reset}>
            Back to product
          </button>
        </div>
      )}

      {/* ── Sheet (manager + form) ── */}
      {(stage === "manager" || stage === "addForm") && (
        <div className='sheet' style={sheetPos}>
          <div className='handle' />

          {/* Manager */}
          {stage === "manager" && (
            <>
              <div className='sheet-hdr'>
                <div>
                  <p className='sheet-lbl'>Sub Units</p>
                  <p className='sheet-name'>{productToEdit.name}</p>
                </div>
                <div className='base-tag'>
                  Base: <strong>{productToEdit.unit}</strong>
                </div>
              </div>

              <div className='su-list'>
                {subUnits.length === 0 && (
                  <div className='su-empty'>
                    <svg width='40' height='40' viewBox='0 0 24 24' fill='none' stroke='#dde5f0' strokeWidth='1.5'>
                      <path d='M20 7H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z' />
                      <path d='M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16' />
                    </svg>
                    <p className='su-empty-txt'>No sub units added yet</p>
                  </div>
                )}
                {subUnits.map((su, i) => (
                  <div key={su.id} className='su-row'>
                    <div className='su-num'>{i + 1}</div>
                    <div className='su-info'>
                      <span className='su-n'>{su.name}</span>
                      <span className='su-m'>
                        {su.quantity}× {su.name} = 1 {productToEdit.unit}
                      </span>
                    </div>
                    <div className='su-price'>
                      {currency} {Number(su.price).toLocaleString()}
                    </div>
                    <button className='su-del' onClick={() => remove(su.id)} title='Remove'>
                      <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
                        <path d='M18 6L6 18M6 6l12 12' />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>

              {subUnits.length < 5 ? (
                <button className='add-btn' onClick={openForm}>
                  <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
                    <path d='M12 5v14M5 12h14' />
                  </svg>
                  Add sub unit
                  <span className='add-count'>{subUnits.length} / 5</span>
                </button>
              ) : (
                <div className='limit-msg'>✓ Maximum of 5 sub units reached</div>
              )}

              <button className='btn-full' onClick={() => createSubUnits()} disabled={subUnits.length === 0 || loading}>
                {loading ? <ButtonLoaderII color='#fff' /> : "Save sub units"}
              </button>
            </>
          )}

          {/* Add Form */}
          {stage === "addForm" && (
            <>
              <div className='form-hdr'>
                <button className='back-btn' onClick={backToManager}>
                  <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='#2563eb' strokeWidth='2.5'>
                    <path d='M19 12H5M12 5l-7 7 7 7' />
                  </svg>
                </button>
                <div>
                  <p className='sheet-lbl'>New Sub Unit</p>
                  <p className='sheet-name'>{productToEdit.name}</p>
                </div>
              </div>

              <div className='form-body'>
                <label className='f-label'>Sub unit name</label>
                <input className='f-input' name='name' placeholder='e.g. Piece, Pack, Box' value={form.name} onChange={handleFormChange} autoComplete='off' />

                <label className='f-label'>Sub unit price ({currency})</label>
                <input
                  className='f-input'
                  name='price'
                  type='number'
                  inputMode='decimal'
                  placeholder='e.g. 5000'
                  value={form.price}
                  onChange={handleFormChange}
                />

                <label className='f-label'>
                  How many <em style={{ color: "#2563eb" }}>{form.name || "sub units"}</em> make 1 <strong>{productToEdit.unit}</strong>?
                </label>
                <input
                  className='f-input'
                  name='quantity'
                  type='number'
                  inputMode='numeric'
                  placeholder='e.g. 12'
                  value={form.quantity}
                  onChange={handleFormChange}
                />

                {form.name && form.price && form.quantity && Number(form.quantity) > 0 && (
                  <div className='preview'>
                    <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='#16a34a' strokeWidth='2.5' style={{ flexShrink: 0 }}>
                      <path d='M20 6L9 17l-5-5' />
                    </svg>
                    {form.quantity}× {form.name} = 1 {productToEdit.unit}&nbsp;·&nbsp;{currency} {Number(form.price).toLocaleString()} each
                  </div>
                )}

                {formError && <p className='f-error'>⚠ {formError}</p>}

                <button className='btn-full' style={{ marginTop: 18 }} onClick={handleAdd}>
                  Add sub unit
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
